// pkg102a — the priced-confirm copy is tier-generic (the 3-credit tier exists now; the hardcoded '2-credit tier' line would lie on a 3-credit quote) — delivered 2026-07-19
// pkg102 — Official-form lane (D-101a/b/f): an "Official court form (fillable PDF)" choice on the creation form imports the PDF's own AcroForm fields as the module — free, instant, no AI; the card carries an Official form badge and the free-import receipt line
// pkg100b — Review & Activate / Edit Fields gated while extract_state is set (Ryan's screenshot catch: the button opened the mid-extraction embryo review screen, whose Save would overwrite the job's result)
// pkg100a — Netlify hotfix: removed the eslint-disable comment referencing react-hooks/exhaustive-deps (the plugin isn't registered in this repo's config, and an unknown rule in a disable comment fails the CI=true build — the standing pkg-era learning, violated and re-learned)
// pkg100 — the charge gate + job UX (D-100c/f/h/j): Upload & Extract first fetches a zero-spend quote and shows the priced confirm (1 or 2 credits by document size, free-action-aware); the row appears immediately as 'Extracting\u2026' and the list polls until it flips; failed extractions show the reason with the no-credit note; completed cards carry the receipt line — delivered 2026-07-18
// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg60 — Prepare and View Submissions collapse into one "Documents" button (D-58e): the lifecycle view is the workhorse, so it inherits Prepare's highlighted style on active client-portal modules; internal modules show it once fills exist — delivered 2026-07-10
// pkg52 — module cards rebuilt (D-53a): title/badges + meta on top, full-width button row below a divider (7 buttons no longer crush the title into one word per line); Prepare is the highlighted action; Deactivate/Delete quarantined right, Delete restyled to a red outline; positive "Exact-fill" badge; page renamed Document Modules (D-53b) — delivered 2026-07-09
// pkg43 — creation form asks who completes the fields (attorney / client / both, D-43a); Prepare button for modules with attorney fields — delivered 2026-07-04
// pkg39 — "Rebuilt output" badge on Word assembly modules without exact-fill (D-39d) — delivered 2026-07-03
// pkg36 — internal modules: Fill Out button; Assign/Auto-Assign hidden (client-portal concepts) — delivered 2026-07-03
// pkg35 — creation form: audience (client portal / internal) + practice-area choice; uploads create document-assembly modules — delivered 2026-07-03
// pkg21a — case-type auto-assign panel (map module to case types; prompted retroactive apply) — delivered 2026-06-11
// pkg21 — assign-to-clients panel added (D-21.1 a) — delivered 2026-06-11
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const STATUS_META = {
  draft:    { label: 'Draft',    color: '#7c5200', bg: '#fff8e1', border: '#f5c842' },
  active:   { label: 'Active',   color: '#1a3a2a', bg: '#f0faf5', border: '#4caf7d' },
  inactive: { label: 'Inactive', color: '#555',    bg: '#f5f5f5', border: '#ccc' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.inactive;
  return (
    <span style={{
      fontSize: 11, fontFamily: 'Arial, sans-serif',
      padding: '2px 8px', borderRadius: 3,
      background: m.bg, color: m.color,
      border: `1px solid ${m.border}`, fontWeight: 600,
    }}>{m.label}</span>
  );
}

export default function AdminFirmModulesPage() {
  const navigate = useNavigate();
  const fileRef  = useRef(null);

  const [modules,  setModules]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [file,     setFile]     = useState(null);
  const [label,    setLabel]    = useState('');
  const [desc,     setDesc]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [busy,     setBusy]     = useState('');

  // pkg35 — creation options: who fills it and what practice area it belongs to.
  const [audience,       setAudience]       = useState('client');
  const [isOfficialForm, setIsOfficialForm] = useState(false); // pkg102 — the pdf_form lane
  const [completedBy,    setCompletedBy]    = useState('client'); // pkg43
  const [practiceAreaId, setPracticeAreaId] = useState('');
  const [practiceAreas,  setPracticeAreas]  = useState([]);
  const [paLoaded,       setPaLoaded]       = useState(false);

  useEffect(() => {
    if (showForm && !paLoaded) {
      adminApi.getModulePracticeAreas()
        .then(list => { setPracticeAreas(Array.isArray(list) ? list : []); setPaLoaded(true); })
        .catch(() => setPaLoaded(true)); // dropdown just stays empty; upload still validates server-side
    }
  }, [showForm, paLoaded]);

  function load() {
    setLoading(true); setError('');
    adminApi.listFirmModules()
      .then(data => { setModules(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }
  useEffect(load, []);

  // pkg100 (D-100c) — while any module is extracting, poll the list every 4s
  // so the row flips to its finished state (or the failure reason) without a
  // manual refresh. The interval tears itself down when nothing is running.
  useEffect(() => {
    if (!modules.some(m => m.extract_state === 'extracting')) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [modules]);

  function handleFileChange(e) {
    const f = e.target.files[0];
    setFile(f || null);
    if (f && !label) {
      setLabel(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '));
    }
  }

  async function handleUpload() {
    if (!file) { setUploadErr('Please choose a PDF or Word (.docx) file.'); return; }
    if (audience === 'client' && !practiceAreaId) { // pkg35 — required for client-portal modules
      setUploadErr('Choose a practice area for this client-portal module.');
      return;
    }
    setUploading(true); setUploadErr('');
    try {
      const opts = {
        kind: isOfficialForm ? 'pdf_form' : 'assembly', // pkg102 — official forms take the AcroForm import lane; pkg35 — everything else is document assembly
        audience,
        practice_area_id: practiceAreaId || undefined,
        completed_by: audience === 'client' ? completedBy : undefined, // pkg43 — internal modules are single-filler
      };

      // pkg102 — the official-form lane is FREE and instant: the "quote" leg
      // just counts the PDF's fillable fields so the confirm can say so.
      if (isOfficialForm) {
        if (!/\.pdf$/i.test(file.name)) {
          setUploadErr('Official forms import from the fillable PDF version. Courts publish these as PDFs \u2014 choose that file, or uncheck the official-form option.');
          setUploading(false);
          return;
        }
        const q2 = await adminApi.uploadFirmModule(file, label || undefined, desc || undefined, { ...opts, quote_only: true });
        const ok2 = window.confirm(
          `This fillable form has ${q2.field_count} field${q2.field_count === 1 ? '' : 's'}. Importing is free \u2014 no AI runs, no credits are used.\n\nImport the form now?`
        );
        if (!ok2) { setUploading(false); return; }
        const modP = await adminApi.uploadFirmModule(file, label || undefined, desc || undefined, opts);
        setModules(prev => [modP, ...prev]);
        setShowForm(false);
        setFile(null); setLabel(''); setDesc('');
        setAudience('client'); setPracticeAreaId(''); setIsOfficialForm(false);
        if (fileRef.current) fileRef.current.value = '';
        setUploading(false);
        return;
      }

      // pkg100 (D-100f/j) — the priced confirm gate. The quote costs nothing
      // and creates nothing; the real upload only happens on an explicit OK.
      const q = await adminApi.uploadFirmModule(file, label || undefined, desc || undefined, { ...opts, quote_only: true });
      const priceLine = q.exempt
        ? 'Your firm is complimentary \u2014 building this module uses no credits.'
        : q.free_action_available
          ? `Building this module uses one of your ${q.free_remaining} remaining FREE AI actions.`
          : `Building this module uses ${q.credits} AI credit${q.credits === 1 ? '' : 's'} ($${q.credits}.00).` +
            (q.large_document ? `\n\nThis is a large document, so extraction is priced at the ${q.credits}-credit tier.` : '') +
            `\n\nCurrent balance: ${q.credit_balance} credit${q.credit_balance === 1 ? '' : 's'}.`;
      const ok = window.confirm(priceLine + '\n\nBuild the module now?');
      if (!ok) { setUploading(false); return; } // cancelled \u2014 nothing sent, nothing charged

      const mod = await adminApi.uploadFirmModule(file, label || undefined, desc || undefined, opts);
      setModules(prev => [mod, ...prev]);
      setShowForm(false);
      setFile(null); setLabel(''); setDesc('');
      setAudience('client'); setPracticeAreaId('');
      if (fileRef.current) fileRef.current.value = '';
      // pkg100 (D-100c) \u2014 the row is 'extracting'; the list polls it in.
      // No navigation: the review page needs the schema, which isn't born yet.
    } catch (err) {
      setUploadErr(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleToggle(mod) {
    setBusy(mod.id);
    try {
      let updated;
      if (mod.status === 'active') {
        updated = await adminApi.deactivateFirmModule(mod.id);
      } else {
        updated = await adminApi.activateFirmModule(mod.id);
      }
      setModules(prev => prev.map(m => m.id === mod.id ? { ...m, ...updated } : m));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function handleDelete(mod) {
    if (!window.confirm(`Delete "${mod.label}"? This cannot be undone and will remove all client assignments.`)) return;
    setBusy(mod.id);
    try {
      await adminApi.deleteFirmModule(mod.id);
      setModules(prev => prev.filter(m => m.id !== mod.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  // ── pkg21 — Assign-to-clients panel (D-21.1 a) ─────────────────────────────
  // Opens a modal listing the firm's active clients with checkboxes pre-set
  // from the module's current assignments. Save diffs checked-vs-original and
  // calls assign/unassign with only the changes.
  const [assignFor,      setAssignFor]      = useState(null); // module being assigned, or null
  const [assignClients,  setAssignClients]  = useState([]);   // firm's client list
  const [assignChecked,  setAssignChecked]  = useState(new Set());
  const [assignOriginal, setAssignOriginal] = useState(new Set());
  const [assignFilter,   setAssignFilter]   = useState('');
  const [assignLoading,  setAssignLoading]  = useState(false);
  const [assignSaving,   setAssignSaving]   = useState(false);
  const [assignErr,      setAssignErr]      = useState('');

  async function openAssign(mod) {
    setAssignFor(mod);
    setAssignErr(''); setAssignFilter('');
    setAssignClients([]); setAssignChecked(new Set()); setAssignOriginal(new Set());
    setAssignLoading(true);
    try {
      const [clients, assigned] = await Promise.all([
        adminApi.listClients(),               // server defaults to active clients
        adminApi.getFirmModuleClients(mod.id),
      ]);
      const assignedIds = new Set((assigned || []).map(c => c.id));
      setAssignClients(Array.isArray(clients) ? clients : []);
      setAssignChecked(new Set(assignedIds));
      setAssignOriginal(assignedIds);
    } catch (err) {
      setAssignErr(err.message);
    } finally {
      setAssignLoading(false);
    }
  }

  function toggleAssign(clientId) {
    setAssignChecked(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }

  async function handleAssignSave() {
    if (!assignFor) return;
    const adds    = [...assignChecked].filter(id => !assignOriginal.has(id));
    const removes = [...assignOriginal].filter(id => !assignChecked.has(id));
    if (!adds.length && !removes.length) { setAssignFor(null); return; }
    setAssignSaving(true); setAssignErr('');
    try {
      if (adds.length)    await adminApi.assignFirmModule(assignFor.id, adds);
      if (removes.length) await adminApi.unassignFirmModule(assignFor.id, removes);
      // Reflect the new assignment count on the card without a full reload.
      const newCount = assignChecked.size;
      setModules(prev => prev.map(m => m.id === assignFor.id ? { ...m, client_count: newCount } : m));
      setAssignFor(null);
    } catch (err) {
      setAssignErr(err.message);
    } finally {
      setAssignSaving(false);
    }
  }

  // ── pkg21a — Case-type auto-assign panel ──────────────────────────────────
  // Maps the module to case types. New clients with a matching case type get
  // the module automatically (creation/case-type-change cascade); saving the
  // mapping offers a prompted retroactive apply to existing active clients
  // (D-21a.4 b). Per-client adds/removes stay in the Assign Clients panel.
  const [autoFor,       setAutoFor]       = useState(null); // module being mapped, or null
  const [autoCaseTypes, setAutoCaseTypes] = useState([]);   // firm's enabled case types
  const [autoChecked,   setAutoChecked]   = useState(new Set());
  const [autoLoading,   setAutoLoading]   = useState(false);
  const [autoSaving,    setAutoSaving]    = useState(false);
  const [autoErr,       setAutoErr]       = useState('');

  function prettyPracticeArea(key) {
    return String(key || 'other')
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  async function openAutoAssign(mod) {
    setAutoFor(mod);
    setAutoErr('');
    setAutoCaseTypes([]); setAutoChecked(new Set());
    setAutoLoading(true);
    try {
      const [caseTypes, mapped] = await Promise.all([
        adminApi.getFirmCaseTypes(),
        adminApi.getFirmModuleCaseTypes(mod.id),
      ]);
      setAutoCaseTypes(Array.isArray(caseTypes) ? caseTypes : []);
      setAutoChecked(new Set((mapped || []).map(m => m.case_type_id)));
    } catch (err) {
      setAutoErr(err.message);
    } finally {
      setAutoLoading(false);
    }
  }

  function toggleAutoCaseType(id) {
    setAutoChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Practice-area header check-all (D-21a.1 a): one click maps every case
  // type in the group; indeterminate-style behavior via checked/partial.
  function toggleAutoGroup(groupIds, allChecked) {
    setAutoChecked(prev => {
      const next = new Set(prev);
      groupIds.forEach(id => { if (allChecked) next.delete(id); else next.add(id); });
      return next;
    });
  }

  async function handleAutoSave() {
    if (!autoFor) return;
    setAutoSaving(true); setAutoErr('');
    try {
      const result = await adminApi.setFirmModuleCaseTypes(autoFor.id, [...autoChecked]);
      if (result.pending_clients > 0) {
        const ok = window.confirm(
          `Assign "${autoFor.label}" to ${result.pending_clients} existing active client${result.pending_clients === 1 ? '' : 's'} with matching case types?\n\n` +
          `(New clients with these case types will get it automatically either way. This may re-add matching clients you previously removed by hand.)`
        );
        if (ok) await adminApi.applyFirmModuleCaseTypes(autoFor.id);
      }
      setAutoFor(null);
      load(); // refresh client counts on the cards
    } catch (err) {
      setAutoErr(err.message);
    } finally {
      setAutoSaving(false);
    }
  }

  // pkg52 (D-53a) — block card: title row on top, button row below. The old
  // flex row let a 7-button group crush the title column to one word a line.
  const cardStyle = {
    background: '#fff', border: '1px solid #dde3ec', borderRadius: 6,
    padding: '16px 18px', marginBottom: 12,
  };
  const btnStyle = (variant = 'default') => ({
    fontSize: 13, fontFamily: 'Arial, sans-serif', cursor: 'pointer',
    padding: '6px 14px', borderRadius: 4,
    border: variant === 'danger-outline' ? '1px solid #B85042' : 'none', // pkg52 — Delete is rare; quiet outline, not a red block
    background: variant === 'primary' ? '#0F1838' : variant === 'danger' ? '#B85042' : variant === 'danger-outline' ? '#fff' : '#eef2f7',
    color: variant === 'primary' || variant === 'danger' ? '#fff' : variant === 'danger-outline' ? '#B85042' : '#0F1838',
    fontWeight: 600,
  });

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>Document Modules</h1>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, color: '#666', margin: 0 }}>
            Upload a form or template → AI extracts the fields → clients fill it in their portal → you download a completed Word doc.
          </p>
        </div>
        <button style={btnStyle('primary')} onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancel' : '+ New Module'}
        </button>
      </div>

      {showForm && (
        <div style={{ background: '#f8fafb', border: '1px solid #dde3ec', borderRadius: 6, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontFamily: 'Arial, sans-serif', fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 14 }}>
            Upload a Document
          </h3>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0, marginBottom: 14 }}>
            Upload any document — an agreement, intake form, court form, engagement letter, or internal checklist (PDF or Word .docx).
            The AI builds a step-by-step questionnaire <em>and</em> a template of your document, so answers render into your actual
            document. You review both before anything goes live.
          </p>

          {uploadErr && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 12, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#b91c1c' }}>
              {uploadErr}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Document <span style={{ color: '#B85042' }}>*</span>
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileChange}
              style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Module Name
            </label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Parenting Plan Worksheet"
              style={{ width: '100%', maxWidth: 400, padding: '7px 10px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
            />
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 3 }}>
              Leave blank to use the AI-suggested title from the document.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Description (optional)
            </label>
            <input
              type="text"
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="Short description shown to clients"
              style={{ width: '100%', maxWidth: 500, padding: '7px 10px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
            />
          </div>

          {/* pkg102 — the official-form lane (D-101a): attorney chooses the class */}
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', maxWidth: 560,
                border: isOfficialForm ? '2px solid #0F1838' : '1px solid #ccc',
                borderRadius: 6, padding: '10px 12px', background: isOfficialForm ? '#F0F2F8' : '#fff',
              }}
            >
              <input
                type="checkbox"
                checked={isOfficialForm}
                onChange={e => setIsOfficialForm(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 700 }}>
                  Official court form (fillable PDF)
                </span>
                <span style={{ display: 'block', fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#555', marginTop: 2 }}>
                  Imports the form's own fillable fields as the questionnaire — free, no AI. Generated documents are the
                  official PDF itself, filled and flattened. Use this for court-published forms; leave it off for your
                  firm's own documents (retainers, letters, worksheets).
                </span>
              </span>
            </label>
          </div>

          {/* pkg35 — who fills this module */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
              Who is this module for?
            </label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { value: 'client',   title: 'Client portal',        sub: 'Clients fill it in their portal' },
                { value: 'internal', title: 'Internal / firm only', sub: 'Staff fill it in the admin portal' },
              ].map(opt => (
                <label
                  key={opt.value}
                  style={{
                    flex: '1 1 210px', maxWidth: 280, cursor: 'pointer',
                    border: audience === opt.value ? '2px solid #0F1838' : '1px solid #ccc',
                    borderRadius: 6, padding: '10px 12px', background: '#fff',
                  }}
                >
                  <input
                    type="radio"
                    name="module-audience"
                    value={opt.value}
                    checked={audience === opt.value}
                    onChange={() => setAudience(opt.value)}
                    style={{ marginRight: 8 }}
                  />
                  <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 700 }}>{opt.title}</span>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 2, marginLeft: 22 }}>{opt.sub}</div>
                </label>
              ))}
            </div>
          </div>

          {/* pkg35 — practice-area scope */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Practice area {audience === 'client'
                ? <span style={{ color: '#B85042' }}>*</span>
                : <span style={{ fontWeight: 400, color: '#888' }}>(optional for internal modules)</span>}
            </label>
            <select
              value={practiceAreaId}
              onChange={e => setPracticeAreaId(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, minWidth: 260 }}
            >
              <option value="">— Select a practice area —</option>
              {practiceAreas.map(pa => (
                <option key={pa.id} value={pa.id}>{pa.label}</option>
              ))}
            </select>
            {audience === 'client' && (
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 4 }}>
                After activation, use Auto-Assign to narrow to specific case types — or leave it to apply across the practice area.
              </div>
            )}
          </div>

          {/* pkg43 (D-43a) — who completes the fields */}
          {audience === 'client' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                Who completes the fields?
              </label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { value: 'client',   title: 'Client',   sub: 'The client answers everything' },
                  { value: 'attorney', title: 'Attorney', sub: 'You prepare it; the client only reviews' },
                  { value: 'both',     title: 'Both',     sub: 'AI splits the fields; you adjust on review' },
                ].map(opt => (
                  <label
                    key={opt.value}
                    style={{
                      flex: '1 1 170px', maxWidth: 240, cursor: 'pointer',
                      border: completedBy === opt.value ? '2px solid #0F1838' : '1px solid #ccc',
                      borderRadius: 6, padding: '10px 12px', background: '#fff',
                    }}
                  >
                    <input
                      type="radio"
                      name="module-completed-by"
                      value={opt.value}
                      checked={completedBy === opt.value}
                      onChange={() => setCompletedBy(opt.value)}
                      style={{ marginRight: 8 }}
                    />
                    <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 700 }}>{opt.title}</span>
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 2, marginLeft: 22 }}>{opt.sub}</div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            style={btnStyle('primary')}
            onClick={handleUpload}
            disabled={uploading || !file}
          >
            {uploading ? (isOfficialForm ? 'Importing your form…' : 'Building your module…') : (isOfficialForm ? 'Import Form — free' : 'Upload & Extract')}
          </button>
          {uploading && (
            <span style={{ marginLeft: 12, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555' }}>
              The AI is reading your document and building the questionnaire and template. This can take up to a minute for long documents…
            </span>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', padding: 20 }}>Loading…</div>
      )}

      {!loading && !modules.length && (
        <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', padding: 20, textAlign: 'center' }}>
          No document modules yet. Click "New Module" to upload your first document.
        </div>
      )}

      {modules.map(mod => (
        <div key={mod.id} style={cardStyle}>
          {/* pkg52 — row 1: title + badges get the full card width */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 15, fontWeight: 700 }}>
              {mod.label}
            </span>
            {/* pkg100 — the job states outrank the status badge visually */}
            {mod.extract_state === 'extracting' ? (
              <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#eef3fb', color: '#1a2a4a', border: '1px solid #b9cbe8', fontWeight: 600 }}>
                Extracting…
              </span>
            ) : mod.extract_state === 'failed' ? (
              <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#fdf0f0', color: '#8a1f1f', border: '1px solid #e08080', fontWeight: 600 }}>
                Extraction failed
              </span>
            ) : (
              <StatusBadge status={mod.status} />
            )}
            {mod.audience === 'internal' && ( /* pkg35 */
              <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#eef2f7', color: '#0F1838', border: '1px solid #c9d4e3', fontWeight: 600 }}>
                Internal
              </span>
            )}
            {/* pkg52 (D-53a) — the positive signal: exact-fill is ACTIVE, so
                generated documents are the firm's original file, filled. */}
            {mod.kind === 'assembly' && /\.docx?$/i.test(mod.doc_filename || '') && mod.exact_fill_status === 'active' && (
              <span
                title="Generated documents are your original file, filled in place — fonts, spacing, and letterhead preserved."
                style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#f0faf5', color: '#1a3a2a', border: '1px solid #4caf7d', fontWeight: 600 }}
              >
                Exact-fill
              </span>
            )}
            {/* pkg39 (D-39d) — a Word-sourced assembly module WITHOUT exact-fill
                generates rebuilt (not byte-faithful) output; flag it at a glance.
                Open the module's review screen to see why and to re-extract. */}
            {mod.kind === 'assembly' && /\.docx?$/i.test(mod.doc_filename || '') && mod.exact_fill_status !== 'active' && (
              <span
                title="Generated documents use the rebuilt format, not your original file — open the module to see why and enable exact-fill."
                style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#f5f5f5', color: '#666', border: '1px solid #ccc', fontWeight: 600 }}
              >
                Rebuilt output
              </span>
            )}
            {/* pkg102 — official-form modules fill the original PDF; there is no
                rebuilt path for this kind, so the badge is always the positive one. */}
            {mod.kind === 'pdf_form' && (
              <span
                title="Generated documents are the official PDF itself — filled on its own fields and flattened. Nothing is rebuilt."
                style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#f0faf5', color: '#1a3a2a', border: '1px solid #4caf7d', fontWeight: 600 }}
              >
                Official form
              </span>
            )}
          </div>
          {/* pkg100 (D-100c/h) — job state detail lines */}
          {mod.extract_state === 'extracting' && (
            <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#1a2a4a', margin: '2px 0 6px' }}>
              Building this module from {mod.doc_filename || 'your document'}… large documents can take a few
              minutes. You can leave this page — the credit is only used when it finishes.
            </p>
          )}
          {mod.extract_state === 'failed' && (
            <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#8a1f1f', margin: '2px 0 6px' }}>
              {mod.extract_error || 'Extraction failed — run it again. No credit was used.'}
            </p>
          )}
          {!mod.extract_state && mod.extract_price_credits != null && (
            <p style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#777', margin: '2px 0 6px' }}>
              Built for {mod.extract_price_credits} AI credit{mod.extract_price_credits === 1 ? '' : 's'}.
            </p>
          )}
          {/* pkg102 — the free-import receipt line */}
          {mod.kind === 'pdf_form' && (
            <p style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#777', margin: '2px 0 6px' }}>
              Imported from the fillable PDF{mod.form_json?.fields?.length ? ` — ${mod.form_json.fields.length} fields` : ''} — no AI charge.
            </p>
          )}
          {mod.description && (
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 4 }}>
              {mod.description}
            </div>
          )}
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>
            {mod.client_count || 0} client{mod.client_count !== 1 ? 's' : ''} assigned
            {' · '}
            {mod.submission_count || 0} submission{mod.submission_count !== 1 ? 's' : ''}
            {mod.doc_filename && ` · from ${mod.doc_filename}`}
          </div>

          {/* pkg52 — row 2: the full-width button row. Everyday actions left,
              Prepare highlighted, Deactivate/Delete quarantined right. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid #eef1f5', paddingTop: 12, marginTop: 12 }}>
            {/* pkg100b — the review screen edits schema_json, which does not
                exist until the extraction job lands; opening it mid-job shows
                an empty questionnaire whose Save would overwrite (or race)
                the job's result. Gate it. A failed extraction has nothing to
                review either — Delete (safe: the job self-noops on a vanished
                row) or a fresh upload are the only sensible actions. */}
            {!mod.extract_state && (
              <button
                style={btnStyle(mod.status === 'draft' ? 'primary' : 'default')}
                disabled={busy === mod.id}
                onClick={() => navigate(`/admin/dashboard/firm-modules/${mod.id}/review`)}
              >
                {mod.status === 'draft' ? 'Review & Activate' : 'Edit Fields'}
              </button>
            )}

            {/* pkg36 — internal modules are filled by staff right here */}
            {mod.status === 'active' && mod.audience === 'internal' && (
              <button
                style={btnStyle('primary')}
                disabled={busy === mod.id}
                onClick={() => navigate(`/admin/dashboard/firm-modules/${mod.id}/fill`)}
              >
                Fill Out
              </button>
            )}

            {/* pkg21 — assign panel (D-21.1 a). Backend only assigns active
                modules, so the button appears once the module is live.
                pkg36 — hidden for internal modules (client-portal concept). */}
            {mod.status === 'active' && mod.audience !== 'internal' && (
              <button
                style={btnStyle()}
                disabled={busy === mod.id}
                onClick={() => openAssign(mod)}
              >
                Assign Clients
              </button>
            )}

            {/* pkg21a — case-type auto-assign rules (pkg36 — client-portal modules only) */}
            {mod.status === 'active' && mod.audience !== 'internal' && (
              <button
                style={btnStyle()}
                disabled={busy === mod.id}
                onClick={() => openAutoAssign(mod)}
              >
                Auto-Assign
              </button>
            )}

            {/* pkg60 (D-58e) — ONE Documents button replaces Prepare + View
                Submissions: the lifecycle view shows every assigned client's
                pipeline and holds Prepare, downloads, share, edit, delete. */}
            {mod.status === 'active' && mod.audience !== 'internal' && (
              <button
                style={btnStyle('primary')}
                disabled={busy === mod.id}
                onClick={() => navigate(`/admin/dashboard/firm-modules/${mod.id}/documents`)}
              >
                Documents
              </button>
            )}
            {mod.audience === 'internal' && mod.submission_count > 0 && (
              <button
                style={btnStyle()}
                onClick={() => navigate(`/admin/dashboard/firm-modules/${mod.id}/documents`)}
              >
                Documents
              </button>
            )}

            <span style={{ flex: 1 }} />

            {mod.status !== 'draft' && (
              <button
                style={mod.status === 'active'
                  ? { ...btnStyle(), background: 'none', color: '#666' } // pkg52 — quiet: it's rarely needed
                  : btnStyle('primary')}
                disabled={busy === mod.id}
                onClick={() => handleToggle(mod)}
              >
                {busy === mod.id ? '…' : mod.status === 'active' ? 'Deactivate' : 'Activate'}
              </button>
            )}

            <button
              style={btnStyle('danger-outline')}
              disabled={busy === mod.id}
              onClick={() => handleDelete(mod)}
            >
              Delete
            </button>
          </div>
        </div>
      ))}

      {/* ── pkg21 — Assign Clients modal ──────────────────────────────────── */}
      {assignFor && (
        <div
          onClick={() => { if (!assignSaving) setAssignFor(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, maxWidth: 480, width: '100%',
              padding: 22, fontFamily: 'Arial, sans-serif',
              maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontFamily: 'Georgia, serif', color: '#0F1838' }}>
                Assign Clients
              </h2>
              <button
                type="button"
                onClick={() => { if (!assignSaving) setAssignFor(null); }}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#888', cursor: 'pointer', padding: 0, marginLeft: 12 }}
              >
                ×
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#555', marginTop: 0, marginBottom: 12 }}>
              Choose which clients see <strong>{assignFor.label}</strong> in their portal.
              Unchecking removes the module from that client.
            </p>

            {assignErr && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: '#b91c1c' }}>
                {assignErr}
              </div>
            )}

            {assignLoading ? (
              <div style={{ color: '#888', fontSize: 13, padding: '16px 0' }}>Loading clients…</div>
            ) : (
              <>
                <input
                  type="text"
                  value={assignFilter}
                  onChange={e => setAssignFilter(e.target.value)}
                  placeholder="Filter by name…"
                  style={{ padding: '7px 10px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, marginBottom: 10, boxSizing: 'border-box', width: '100%' }}
                />
                <div style={{ overflowY: 'auto', border: '1px solid #e5e9f0', borderRadius: 5, flex: 1, minHeight: 120 }}>
                  {assignClients
                    .filter(c => !assignFilter || (c.name || '').toLowerCase().includes(assignFilter.toLowerCase()))
                    .map(c => (
                      <label
                        key={c.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f0f2f6', cursor: 'pointer', fontSize: 13 }}
                      >
                        <input
                          type="checkbox"
                          checked={assignChecked.has(c.id)}
                          onChange={() => toggleAssign(c.id)}
                          style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                        />
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                        {c.email && <span style={{ color: '#888', fontSize: 12, marginLeft: 'auto' }}>{c.email}</span>}
                      </label>
                    ))}
                  {!assignClients.length && (
                    <div style={{ color: '#888', fontSize: 13, padding: 14, textAlign: 'center' }}>No active clients found.</div>
                  )}
                </div>
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    {assignChecked.size} client{assignChecked.size === 1 ? '' : 's'} selected
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={btnStyle()} onClick={() => setAssignFor(null)} disabled={assignSaving}>
                      Cancel
                    </button>
                    <button style={btnStyle('primary')} onClick={handleAssignSave} disabled={assignSaving}>
                      {assignSaving ? 'Saving…' : 'Save Assignments'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── pkg21a — Auto-Assign (case types) modal ───────────────────────── */}
      {autoFor && (
        <div
          onClick={() => { if (!autoSaving) setAutoFor(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, maxWidth: 520, width: '100%',
              padding: 22, fontFamily: 'Arial, sans-serif',
              maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontFamily: 'Georgia, serif', color: '#0F1838' }}>
                Auto-Assign by Case Type
              </h2>
              <button
                type="button"
                onClick={() => { if (!autoSaving) setAutoFor(null); }}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#888', cursor: 'pointer', padding: 0, marginLeft: 12 }}
              >
                ×
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#555', marginTop: 0, marginBottom: 12 }}>
              <strong>{autoFor.label}</strong> will be assigned automatically to every new client
              created with a checked case type. Saving offers to also assign existing clients.
              Check a practice-area heading to select all its case types.
            </p>

            {autoErr && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: '#b91c1c' }}>
                {autoErr}
              </div>
            )}

            {autoLoading ? (
              <div style={{ color: '#888', fontSize: 13, padding: '16px 0' }}>Loading case types…</div>
            ) : (
              <>
                <div style={{ overflowY: 'auto', border: '1px solid #e5e9f0', borderRadius: 5, flex: 1, minHeight: 120 }}>
                  {Object.entries(
                    autoCaseTypes.reduce((groups, ct) => {
                      const k = ct.practice_area || 'other';
                      (groups[k] = groups[k] || []).push(ct);
                      return groups;
                    }, {})
                  ).map(([area, types]) => {
                    const groupIds   = types.map(t => t.id);
                    const allChecked = groupIds.every(id => autoChecked.has(id));
                    return (
                      <div key={area}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafb', borderBottom: '1px solid #e5e9f0', cursor: 'pointer', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#444' }}>
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={() => toggleAutoGroup(groupIds, allChecked)}
                            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                          />
                          {prettyPracticeArea(area)}
                        </label>
                        {types.map(ct => (
                          <label
                            key={ct.id}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px 8px 28px', borderBottom: '1px solid #f0f2f6', cursor: 'pointer', fontSize: 13 }}
                          >
                            <input
                              type="checkbox"
                              checked={autoChecked.has(ct.id)}
                              onChange={() => toggleAutoCaseType(ct.id)}
                              style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                            />
                            <span style={{ fontWeight: 600 }}>{ct.label}</span>
                            <span style={{ color: '#888', fontSize: 12, marginLeft: 'auto' }}>
                              {Number(ct.active_client_count) || 0} active client{Number(ct.active_client_count) === 1 ? '' : 's'}
                            </span>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                  {!autoCaseTypes.length && (
                    <div style={{ color: '#888', fontSize: 13, padding: 14, textAlign: 'center' }}>No case types enabled for this firm.</div>
                  )}
                </div>
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    {autoChecked.size} case type{autoChecked.size === 1 ? '' : 's'} selected
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={btnStyle()} onClick={() => setAutoFor(null)} disabled={autoSaving}>
                      Cancel
                    </button>
                    <button style={btnStyle('primary')} onClick={handleAutoSave} disabled={autoSaving}>
                      {autoSaving ? 'Saving…' : 'Save Rules'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
